import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Lock, Loader2 } from "lucide-react";
import { PasswordField } from "../components/PasswordField";
import api from "../api";
import { useToast } from "../hooks/use-toast";
import { useLang } from "../context/LanguageContext";

const ResetPassword = () => {
  const { t } = useLang();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { toast } = useToast();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  // The bar depends on whose account it is — a shop or admin is held to 12 characters, the same
  // as when the account was created, so the meter can never disagree with the server.
  const [policy, setPolicy] = useState(null);
  const [acceptWeak, setAcceptWeak] = useState(false);

  useEffect(() => {
    if (!token) { setPolicy({ valid: false }); return; }
    api.get(`/auth/reset-password/policy?token=${encodeURIComponent(token)}`)
      .then((r) => setPolicy(r.data))
      .catch(() => setPolicy({ valid: true, role: "shopper", min: 8 }));
  }, [token]);

  const min = policy?.min || 8;

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < min) { toast({ title: t("rst.passwordTooShort"), description: t("rst.minChars", { n: min }) }); return; }
    if (pw !== pw2) { toast({ title: t("rst.passwordsDonTMatch") }); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, newPassword: pw, acceptWeak });
      toast({ title: t("rst.passwordReset"), description: t("rst.youCanNowLog") });
      navigate("/login");
    } catch (err) {
      toast({ title: t("rst.couldnTResetPassword"), description: err.response?.data?.detail || "The link may be invalid or expired." });
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary/50 px-5">
      <div className="w-full max-w-md">
        <Link to="/" className="flex flex-col items-center gap-2 mb-6">
          <img src="/lp-logo.webp" alt="LytePress" className="h-16 w-16 rounded-full border border-border shadow-sm" />
          <span className="text-xl font-extrabold text-foreground">LytePress</span>
        </Link>
        <div className="bg-card border border-border rounded-2xl shadow-xl p-8" data-testid="reset-password-page">
          <h1 className="text-2xl font-extrabold text-foreground text-center mb-1">{t("rst.setANewPassword")}</h1>
          <p className="text-sm text-muted-foreground text-center mb-6">{t("rst.chooseAStrongPassword")}</p>
          {!token || policy?.valid === false ? (
            <p className="text-center text-sm text-rose-500" data-testid="reset-link-invalid">{t("rst.thisResetLinkIs")} <Link to="/forgot-password" className="font-semibold underline">{t("rst.forgotPassword")}</Link>.</p>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <PasswordField value={pw} onChange={(e) => setPw(e.target.value)} role={policy?.role || "shopper"}
                placeholder={t("rst.newPassword")} testid="reset-password-input"
                acceptWeak={acceptWeak} onAcceptWeak={policy?.canOverride ? setAcceptWeak : undefined} />
              {!pw && <p className="text-xs text-muted-foreground" data-testid="reset-password-rule">{t("rst.minChars", { n: min })}</p>}
              <div className="flex items-center gap-3 border border-border rounded-xl px-4 py-3"><Lock size={18} className="text-muted-foreground" /><input type="password" required value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder={t("rst.confirmNewPassword")} className="w-full bg-transparent outline-none text-sm text-foreground" data-testid="reset-password-confirm" /></div>
              <button disabled={loading} data-testid="reset-submit" className="w-full bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2">{loading && <Loader2 className="animate-spin" size={18} />} Reset password</button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
